import { Box, Grid, useTheme } from '@chronosphereio/chrono-ui';
import { useEffect, useMemo } from 'react';
import { CompareOptions, StatChartBase, useSeriesColorGenerator } from '@/components';
import { useFindThreshold } from '@/core/utils/thresholds';
import { useCompareMode, useDataQueryResult, useVizPanelColor } from '@/plugin-system';
import { NoData } from '@/plugin-system/components/NoData';
import { StatChartVirtualizedGrid } from './StatChartVirtualizedGrid';
import {
  STAT_CHART_DATA_TYPES,
  STAT_CHART_MIN_WIDTH,
  StatChartColorScheme,
  StatChartPanelLoadedProps,
} from './stat-chart-model';
import { calculateSeriesDisplayProps, shouldShowSeriesName, useStatChartData } from './utils/data-transform';

const SPACING = 8;

/**
 * When greater than this number of series, react-virtuoso will be used to improve performance.
 */
const VIRTUALIZATION_THRESHOLD = 50;

export const defaultThresholds: NonNullable<StatChartPanelLoadedProps['thresholds']> = {};

export function StatChartPanelLoaded(props: StatChartPanelLoadedProps) {
  const { setColor } = useVizPanelColor();

  const {
    calculation,
    description,
    unit,
    sparkline,
    valueFontSize,
    seriesNameFontSize,
    descriptionFontSize,
    messageFontSize,
    contentDimensions,
    colorScheme: colorSchemeProp,
    showSeriesName: showSeriesNameProp,
  } = props;

  // Default to "auto" if undefined
  const showSeriesNameWithFallback = showSeriesNameProp ?? 'Auto';
  const colorScheme: StatChartColorScheme = colorSchemeProp ?? StatChartColorScheme.DEFAULT;

  const thresholds = props.thresholds ?? defaultThresholds;

  const colorGenerator = useSeriesColorGenerator();
  const sparklineFallbackColor = colorGenerator.palette.Categorical[0];
  useMemo(() => {
    // if there is a sparkline but no color is not specified, fall back to the first color in the categorical palette
    if (sparkline !== undefined && !sparkline?.color) {
      // eslint-disable-next-line react-hooks/immutability
      sparkline.color = sparklineFallbackColor;
    }
  }, [sparkline, sparklineFallbackColor]);

  const { queryResults } = useDataQueryResult({ dataTypeFilter: STAT_CHART_DATA_TYPES });
  const statChartData = useStatChartData(queryResults, { calculation, thresholds, sparkline, colorScheme });
  const numCharts = statChartData.length;
  const isMultiSeries = numCharts > 1;

  if (queryResults[0]?.error) throw queryResults[0]?.error;

  // Calculates chart width
  const panelPadding = isMultiSeries ? 8 : 0;
  const panelWidth = Math.floor(contentDimensions.width - panelPadding * 2); // Math.floor here to lose any partial pixels;
  let panelWidthMinusSpacing = panelWidth - SPACING * (numCharts - 1);
  let chartWidth = Math.floor(panelWidthMinusSpacing / numCharts); // round down so that the sum of the chart widths does not exceed panel width
  let chartsPerRow = Math.floor(panelWidthMinusSpacing / chartWidth);
  let multipleRows = false;

  if (isMultiSeries && chartWidth < STAT_CHART_MIN_WIDTH) {
    multipleRows = true;
    // Ensure the row is evenly divided by the number of charts to eliminate any empty space at the end of the row
    chartsPerRow = Math.floor(panelWidth / (STAT_CHART_MIN_WIDTH + SPACING));
    const spacingPerRow = SPACING * (chartsPerRow - 1);
    chartWidth = Math.floor((panelWidth - spacingPerRow) / chartsPerRow);
    panelWidthMinusSpacing = panelWidth - spacingPerRow;
  }

  // Keep track of the remaining pixels after the charts. 1px of this remainder will be added to each chart until it runs out.
  // This ensures that the charts take up the panel width perfectly.
  const remainder = panelWidthMinusSpacing % chartsPerRow;

  const { height, width } = contentDimensions;
  let chartHeight = chartWidth;
  // If the chart is taller than the height of the panel, set the chart size to the height of the panel
  if (chartHeight > height) {
    chartHeight = height;
  }

  const { compareMode } = useCompareMode();
  const comparisonPeriodLabel = CompareOptions.find((option) => option.key === compareMode)?.label.toLowerCase();

  const { theme } = useTheme();

  const findThreshold = useFindThreshold(thresholds);
  const singleSeriesValue = numCharts === 1 ? statChartData[0]?.calculatedValue : undefined;
  const singleSeriesThreshold =
    singleSeriesValue !== undefined && singleSeriesValue !== null ? findThreshold(singleSeriesValue) : undefined;

  useEffect(() => {
    if (
      singleSeriesValue !== undefined &&
      singleSeriesValue !== null &&
      singleSeriesThreshold !== undefined &&
      singleSeriesThreshold.background_color !== undefined
    ) {
      setColor({
        background: singleSeriesThreshold.background_color,
        color: singleSeriesThreshold.color,
      });
    } else {
      setColor({
        background: undefined,
        color: undefined,
      });
    }

    return () =>
      setColor({
        background: undefined,
        color: undefined,
      });
  }, [singleSeriesThreshold, singleSeriesValue, setColor]);

  const showSeriesName = shouldShowSeriesName(showSeriesNameWithFallback, numCharts);

  const renderContent = () => {
    if (!statChartData.length) {
      return (
        <Box display="flex" alignItems="center" height="100%" justifyContent="center">
          <NoData />
        </Box>
      );
    }

    if (numCharts > VIRTUALIZATION_THRESHOLD) {
      return (
        <StatChartVirtualizedGrid
          statChartData={statChartData}
          contentWidth={panelWidth}
          chartHeight={multipleRows ? chartHeight : height}
          unit={unit}
          thresholds={thresholds}
          isMultiSeries={isMultiSeries}
          valueFontSize={valueFontSize}
          seriesNameFontSize={seriesNameFontSize}
          descriptionFontSize={descriptionFontSize}
          messageFontSize={messageFontSize}
          description={description}
          colorScheme={colorScheme}
          showSeriesName={showSeriesName}
          comparisonPeriodLabel={comparisonPeriodLabel}
          valueAlign={props.valueAlign}
        />
      );
    }

    return (
      <Grid container alignItems="center" spacing={`${SPACING}px`} paddingX={`${panelPadding}px`}>
        {statChartData.map((series, index) => {
          const { threshold, showSeriesNameAsMessage, calculatedShowSeriesName } = calculateSeriesDisplayProps({
            calculatedValue: series.calculatedValue,
            findThreshold,
            showSeriesName,
          });

          return (
            <Grid item key={index} height="100%">
              <StatChartBase
                width={index % chartsPerRow < remainder ? chartWidth + 1 : chartWidth}
                height={multipleRows ? chartHeight : height}
                data={series}
                unit={unit}
                color={colorScheme === 'thresholds' ? series.color : theme.palette.text.primary}
                sparkline={series.sparkline}
                isMultiSeries={isMultiSeries}
                valueFontSize={valueFontSize}
                seriesNameFontSize={seriesNameFontSize}
                descriptionFontSize={descriptionFontSize}
                messageFontSize={messageFontSize}
                comparison={series.comparison}
                comparisonPeriodLabel={comparisonPeriodLabel}
                description={description}
                message={showSeriesNameAsMessage ? series.seriesData?.name : threshold.name}
                hideValue={threshold.show_value === false}
                backgroundColor={threshold.background_color}
                showSeriesName={calculatedShowSeriesName}
                valueAlign={props.valueAlign}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box width={width} height={height} overflow="auto">
      {renderContent()}
    </Box>
  );
}
